import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { saveMemberRegistrationDetails, getMemberRegistrationDetails } from '../services/api';
import { getLeads } from '../redux/leadsSlice';

import Header from '../components/Header';
import Footer from '../components/Footer';
import Loader from '../components/ui/loader/Loader';

import { PREDEFINED_CITIES, PREDEFINED_STATES } from '../utils/constants';

const MemberRegister = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();

    const { data: leads = [], status } = useSelector((state) => state.leads);
    // const { registrationDetails } = useSelector((state) => state.member); // Redux slice not used anymore

    useEffect(() => {
        if (status === 'idle') {
            dispatch(getLeads({ page: 1, type: "all" }));
        }
    }, [dispatch, status]);

    const [formData, setFormData] = useState({
        full_name: '',
        mobile_country_code: '+91',
        mobile_phone: '',
        email: '',
        address1: '',
        address2: '',
        state: '',
        city: '',
        pincode: ''
    });

    const [errors, setErrors] = useState({});
    const [validatedFields, setValidatedFields] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState('');
    const [apiMessage, setApiMessage] = useState('');
    const [registeredMemberId, setRegisteredMemberId] = useState('');
    const [isExistingUser, setIsExistingUser] = useState(false);
    const [showLoader, setShowLoader] = useState(false);
    const [loaderText, setLoaderText] = useState('Processing, please wait...');

    // City Suggestion State
    const [filteredCities, setFilteredCities] = useState([]);
    const [showCitySuggestions, setShowCitySuggestions] = useState(false);
    const cityWrapperRef = useRef(null);

    useEffect(() => {
        function handleClickOutside(event) {
            if (cityWrapperRef.current && !cityWrapperRef.current.contains(event.target)) {
                setShowCitySuggestions(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [cityWrapperRef]);

    const handleCityKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            setShowCitySuggestions(false);
        }
    };

    const handleCityChange = (e) => {
        const value = e.target.value;
        setFormData(prev => ({ ...prev, city: value }));

        if (value.trim()) {
            const filtered = PREDEFINED_CITIES.filter(city =>
                city.toLowerCase().includes(value.toLowerCase())
            );
            setFilteredCities(filtered);
            setShowCitySuggestions(true);
        } else {
            setShowCitySuggestions(false);
        }

        if (errors.city) {
            setErrors(prev => ({ ...prev, city: '' }));
        }
    };

    const handleSelectCity = (city) => {
        setFormData(prev => ({ ...prev, city: city }));
        setShowCitySuggestions(false);
        if (errors.city) {
            setErrors(prev => ({ ...prev, city: '' }));
        }
    };

    // Pre-select deal from URL if present
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        // User requested to keep spaces/special chars as is, no hyphen replacement
        const dealParam = searchParams.get('deal');
        if (dealParam) {
            setFormData(prev => ({ ...prev, intrested_in_deals: dealParam }));
        }
    }, [location.search]);

    // Removed auto-fill from Redux as requested

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // If deal changes manually, update the URL params to sync
        if (name === 'intrested_in_deals') {
            const searchParams = new URLSearchParams(location.search);
            if (value) {
                // Keep raw value / standard encoding
                searchParams.set('deal', value);
            } else {
                searchParams.delete('deal');
            }
            navigate({ pathname: location.pathname, search: searchParams.toString() }, { replace: true });
        }

        // Clear error
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleMobileChange = (e) => {
        const value = e.target.value.replace(/\D/g, '');
        if (value.length <= 10) {
            setFormData(prev => ({ ...prev, mobile_phone: value }));
            if (errors.mobile_phone) {
                setErrors(prev => ({ ...prev, mobile_phone: '' }));
            }
        }
    };

    const validateMobileOnBlur = () => {
        if (formData.mobile_phone.trim()) {
            if (!/^\d{10}$/.test(formData.mobile_phone)) {
                setErrors(prev => ({ ...prev, mobile_phone: 'Mobile must be exactly 10 digits' }));
                setValidatedFields(prev => ({ ...prev, mobile_phone: false }));
            } else {
                setErrors(prev => ({ ...prev, mobile_phone: '' }));
                setValidatedFields(prev => ({ ...prev, mobile_phone: true }));
            }
        }
    };

    const validateEmailOnBlur = () => {
        if (formData.email.trim()) {
            if (!/^[a-zA-Z0-9._\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,6}$/.test(formData.email)) {
                setErrors(prev => ({ ...prev, email: 'Please enter a valid email address' }));
                setValidatedFields(prev => ({ ...prev, email: false }));
            } else {
                setErrors(prev => ({ ...prev, email: '' }));
                setValidatedFields(prev => ({ ...prev, email: true }));
            }
        }
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.full_name?.trim()) newErrors.full_name = 'Full Name is required';
        if (!formData.mobile_phone?.trim()) newErrors.mobile_phone = 'Mobile is required';
        else if (!/^\d{10}$/.test(formData.mobile_phone)) newErrors.mobile_phone = 'Mobile must be exactly 10 digits';

        if (!formData.email?.trim()) newErrors.email = 'Email is required';
        else if (!/^[a-zA-Z0-9._\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,6}$/.test(formData.email)) newErrors.email = 'Invalid email';

        if (!formData.address1?.trim()) newErrors.address1 = 'Address Line 1 is required';
        if (!formData.state?.trim()) newErrors.state = 'State is required';
        if (!formData.city?.trim()) newErrors.city = 'City is required';
        if (!formData.pincode?.trim()) newErrors.pincode = 'Pincode is required';
        else if (!/^\d{6}$/.test(formData.pincode)) newErrors.pincode = 'Pincode must be 6 digits';

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setIsSubmitting(true);
        setLoaderText('Processing, please wait...');
        setShowLoader(true);
        setSubmitStatus('');
        setApiMessage('');
        setIsExistingUser(false);

        try {
            const res = await saveMemberRegistrationDetails(formData);
            const data = await res.json();

            // console.log("data", data);

            if (data.message) {
                const { userExists, user, mem_reg_id, message } = data.message;

                if (userExists === 1) {
                    setSubmitStatus('error');
                    setApiMessage("This mobile number is already associated with an existing registration" || 'This mobile number is already associated with an existing registration');
                    setIsExistingUser(true);

                    // Prioritize mem_reg_id as the Member ID
                    if (mem_reg_id) setRegisteredMemberId(mem_reg_id);
                    else if (user && user.memberId) setRegisteredMemberId(user.memberId);
                    else if (user && user.name) setRegisteredMemberId(user.name);

                    // Redux dispatch removed

                } else {
                    setSubmitStatus('success');

                    // If we have a mem_reg_id, use that as the identifier
                    if (mem_reg_id) {
                        setRegisteredMemberId(mem_reg_id);
                    } else if (user && user.name) {
                        setRegisteredMemberId(user.name);
                    }

                    // Redux dispatch removed
                }
            } else {
                setSubmitStatus('error');
                setApiMessage(data.message?.message || 'Registration failed');
            }
        } catch (error) {
            console.error(error);
            setSubmitStatus('error');
            setApiMessage('Something went wrong. Please try again.');
        } finally {
            setIsSubmitting(false);
            setShowLoader(false);
        }
    };

    const handleBecomeMemberClick = () => {
        setLoaderText('Processing to become a member registration...');
        setShowLoader(true);
        const memberId = registeredMemberId || '';
        let targetUrl = `/become-member?MEM_REG_ID=${memberId}`;

        // Preserve deal param if exists or from form
        const searchParams = new URLSearchParams(location.search);
        const currentDeal = searchParams.get('deal');

        let dealToSend = '';
        if (formData.intrested_in_deals) {
            // User requested to prioritize dropdown value for the next page URL
            dealToSend = formData.intrested_in_deals;
        } else if (currentDeal) {
            dealToSend = currentDeal;
        }

        if (dealToSend) {
            // Encode safely
            targetUrl += `&deal=${encodeURIComponent(dealToSend)}`;
        }

        // Small delay to show loader before navigating
        setTimeout(() => {
            navigate(targetUrl);
        }, 500);
    };



    return (
        <main>
            <section className='cbp_layout_header'>
                <div className='container-fluid grey-border-b-1'>
                    <Header></Header>
                </div>
            </section>
            <section className='cbp_layout_member' id='member_container'>
                <div className='container'>
                    {showLoader && <Loader message={loaderText} />}
                    <div className="row justify-content-center mb-md-5">
                        <div className="col-12 col-md-6 col-lg-6">
                            <div className="membership-form-container mt-3">
                                <div className="form-section mb-2">
                                    {/* Success Message UI */}
                                    {(submitStatus === 'success' || (submitStatus === 'error' && isExistingUser)) ? (
                                        <div className="text-center mb-5 mt-4">
                                            <div className={isExistingUser ? "text-danger mb-3" : "text-success mb-3"}>
                                                {isExistingUser ? (
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" fill="currentColor" className="bi bi-exclamation-circle-fill" viewBox="0 0 16 16">
                                                        <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM8 4a.905.905 0 0 0-.9.995l.35 3.507a.552.552 0 0 0 1.1 0l.35-3.507A.905.905 0 0 0 8 4zm.002 6a1 1 0 1 0 0 2 1 1 0 0 0 0-2z" />
                                                    </svg>
                                                ) : (
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" fill="currentColor" className="bi bi-check-circle-fill" viewBox="0 0 16 16">
                                                        <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z" />
                                                    </svg>
                                                )}
                                            </div>
                                            <p className="mb-4 deal-message">
                                                {isExistingUser ? (
                                                    apiMessage + " Please click below to:"
                                                ) : (
                                                    <>Thank you for registering. We will connect with you shortly.</>
                                                )}
                                            </p>
                                            <button
                                                className="btn btn-primary sin-button px-4 py-3 rounded-pill shadow-sm border-0"
                                                onClick={handleBecomeMemberClick}
                                                style={{ background: 'linear-gradient(to bottom, #0082CB 0%, #004E7A 100%)' }}
                                            >
                                                Continue to Become a Member
                                            </button>
                                        </div>
                                    ) : (
                                        <>
                                            <h3 className="section-heading mb-0 text-center">
                                                Connect With Us
                                            </h3>
                                            <p className="mb-5 text-center" style={{ fontSize: '14px' }}>
                                                Please fill below membership details. CBP Team will call you for further process.
                                            </p>

                                            {/* Error Message */}
                                            {submitStatus === 'error' && !isExistingUser && (
                                                <div className="alert alert-danger mb-4">
                                                    {apiMessage}
                                                </div>
                                            )}

                                            <form onSubmit={handleSubmit}>
                                                <div className="row">
                                                    {/* Full Name */}
                                                    <div className="col-12 col-md-6">
                                                        <div className="form-group">
                                                            <label className="form-label">Full Name <span className="text-danger">*</span></label>
                                                            <input
                                                                type="text"
                                                                name="full_name"
                                                                placeholder="Enter your full name"
                                                                value={formData.full_name}
                                                                onChange={handleInputChange}
                                                                className={`form-input${errors.full_name ? ' error' : ''}`}
                                                                disabled={isSubmitting}
                                                            />
                                                            {errors.full_name && <span className="error-message">{errors.full_name}</span>}
                                                        </div>
                                                    </div>

                                                    {/* Mobile */}
                                                    <div className="col-12 col-md-6">
                                                        <div className="form-group">
                                                            <label className="form-label">Mobile Number <span className="text-danger">*</span></label>
                                                            <div className="d-flex">
                                                                <select
                                                                    name="mobile_country_code"
                                                                    value={formData.mobile_country_code}
                                                                    onChange={handleInputChange}
                                                                    className="form-input"
                                                                    style={{ width: '80px', marginRight: '10px' }}
                                                                    disabled={isSubmitting}
                                                                >
                                                                    <option value="+91">+91</option>
                                                                    <option value="+1">+1</option>
                                                                    <option value="+44">+44</option>
                                                                </select>
                                                                <div className="input-with-validation flex-grow-1">
                                                                    <input
                                                                        type="tel"
                                                                        name="mobile_phone"
                                                                        placeholder="Enter 10 digit number"
                                                                        value={formData.mobile_phone}
                                                                        onChange={handleMobileChange}
                                                                        onBlur={validateMobileOnBlur}
                                                                        className={`form-input${errors.mobile_phone ? ' error' : ''} ${validatedFields.mobile_phone ? ' validated' : ''}`}
                                                                        maxLength={10}
                                                                        inputMode="numeric"
                                                                        disabled={isSubmitting}
                                                                    />
                                                                    {validatedFields.mobile_phone && <span className="validation-check">✓</span>}
                                                                </div>
                                                            </div>
                                                            {errors.mobile_phone && <span className="error-message">{errors.mobile_phone}</span>}
                                                        </div>
                                                    </div>

                                                    {/* Email */}
                                                    <div className="col-12 col-md-6">
                                                        <div className="form-group">
                                                            <label className="form-label">Email ID <span className="text-danger">*</span></label>
                                                            <div className="input-with-validation">
                                                                <input
                                                                    type="email"
                                                                    name="email"
                                                                    placeholder="example@domain.com"
                                                                    value={formData.email}
                                                                    onChange={handleInputChange}
                                                                    onBlur={validateEmailOnBlur}
                                                                    className={`form-input${errors.email ? ' error' : ''} ${validatedFields.email ? ' validated' : ''}`}
                                                                    disabled={isSubmitting}
                                                                />
                                                                {validatedFields.email && <span className="validation-check">✓</span>}
                                                            </div>
                                                            {errors.email && <span className="error-message">{errors.email}</span>}
                                                        </div>
                                                    </div>

                                                    {/* Address 1 */}
                                                    <div className="col-12 col-md-6">
                                                        <div className="form-group">
                                                            <label className="form-label">Address Line 1 <span className="text-danger">*</span></label>
                                                            <input
                                                                type="text"
                                                                name="address1"
                                                                placeholder="House/Flat No, Building, Street"
                                                                value={formData.address1}
                                                                onChange={handleInputChange}
                                                                className={`form-input${errors.address1 ? ' error' : ''}`}
                                                                disabled={isSubmitting}
                                                            />
                                                            {errors.address1 && <span className="error-message">{errors.address1}</span>}
                                                        </div>
                                                    </div>

                                                    {/* Address 2 */}
                                                    <div className="col-12 col-md-6">
                                                        <div className="form-group">
                                                            <label className="form-label">Address Line 2</label>
                                                            <input
                                                                type="text"
                                                                name="address2"
                                                                placeholder="Area, Colony (Optional)"
                                                                value={formData.address2}
                                                                onChange={handleInputChange}
                                                                className="form-input"
                                                                disabled={isSubmitting}
                                                            />
                                                        </div>
                                                    </div>

                                                    {/* State */}
                                                    <div className="col-12 col-md-6">
                                                        <div className="form-group">
                                                            <label className="form-label">State <span className="text-danger">*</span></label>
                                                            <select
                                                                name="state"
                                                                value={formData.state}
                                                                onChange={handleInputChange}
                                                                className={`form-input${errors.state ? ' error' : ''}`}
                                                                disabled={isSubmitting}
                                                            >
                                                                <option value="">Select State</option>
                                                                {PREDEFINED_STATES.map((st) => (
                                                                    <option key={st} value={st}>
                                                                        {st}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                            {errors.state && <span className="error-message">{errors.state}</span>}
                                                        </div>
                                                    </div>

                                                    {/* City */}
                                                    <div className="col-12 col-md-6">
                                                        <div className="form-group" ref={cityWrapperRef} style={{ position: 'relative' }}>
                                                            <label className="form-label">City <span className="text-danger">*</span></label>
                                                            <input
                                                                type="text"
                                                                name="city"
                                                                placeholder="Search City"
                                                                value={formData.city}
                                                                onChange={handleCityChange}
                                                                onKeyDown={handleCityKeyDown}
                                                                onFocus={() => {
                                                                    if (formData.city) {
                                                                        const filtered = PREDEFINED_CITIES.filter(city =>
                                                                            city.toLowerCase().includes(formData.city.toLowerCase())
                                                                        );
                                                                        setFilteredCities(filtered);
                                                                    } else {
                                                                        setFilteredCities(PREDEFINED_CITIES);
                                                                    }
                                                                    setShowCitySuggestions(true);
                                                                }}
                                                                className={`form-input${errors.city ? ' error' : ''}`}
                                                                disabled={isSubmitting}
                                                                autoComplete="off"
                                                            />
                                                            {showCitySuggestions && (
                                                                <ul className="list-group position-absolute w-100" style={{ zIndex: 1000, maxHeight: '200px', overflowY: 'auto', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
                                                                    {filteredCities.length > 0 ? (
                                                                        filteredCities.map((city, index) => (
                                                                            <li
                                                                                key={index}
                                                                                className="list-group-item list-group-item-action"
                                                                                onClick={() => handleSelectCity(city)}
                                                                                style={{ cursor: 'pointer' }}
                                                                            >
                                                                                {city}
                                                                            </li>
                                                                        ))
                                                                    ) : (
                                                                        <li className="list-group-item text-muted">No cities found</li>
                                                                    )}
                                                                </ul>
                                                            )}
                                                            {errors.city && <span className="error-message">{errors.city}</span>}
                                                        </div>
                                                    </div>

                                                    {/* Pincode */}
                                                    <div className="col-12 col-md-6">
                                                        <div className="form-group">
                                                            <label className="form-label">Pincode <span className="text-danger">*</span></label>
                                                            <input
                                                                type="text"
                                                                name="pincode"
                                                                placeholder="6 digit Pincode"
                                                                value={formData.pincode}
                                                                onChange={(e) => {
                                                                    const val = e.target.value.replace(/\D/g, '');
                                                                    if (val.length <= 6) handleInputChange({ target: { name: 'pincode', value: val } });
                                                                }}
                                                                className={`form-input${errors.pincode ? ' error' : ''}`}
                                                                disabled={isSubmitting}
                                                                maxLength={6}
                                                            />
                                                            {errors.pincode && <span className="error-message">{errors.pincode}</span>}
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="form-actions mt-4">
                                                    <button
                                                        type="submit"
                                                        className="btn btn-primary sin-button px-4 py-3 rounded-pill shadow-sm border-0"
                                                        disabled={isSubmitting}
                                                        style={{ background: 'linear-gradient(to bottom, #0082CB 0%, #004E7A 100%)' }}
                                                    >
                                                        {isSubmitting ? 'Submitting...' : 'Continue'}
                                                    </button>
                                                </div>
                                            </form>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            <section className='cbp_layout footer-section'>
                <div className='container'>
                    <Footer></Footer>
                </div>
            </section>
        </main>
    );
};

export default MemberRegister;
